// Validator-income view for <vf-apy-chart> (operator spec C51): one
// per-epoch time-series line of the ledger net_total in SOL, on the same
// grid/slider/tooltip chrome as the other views (chart-options.ts shared
// helpers), triggered by the [validator income] header button — not a band
// tab. Pure: data + theme in, option out.
//
// Honesty rules enforced here:
// - The plotted figure is the served /apy net_total_lamports, integer
//   lamports until the display-edge SOL conversion (solNumber for plot
//   geometry; the tooltip formats from the integer via lamportsToSol
//   inside income-explorer-chart.ts).
// - connectNulls: false and null-preserving series: an epoch without a
//   served value is a visible gap, never 0, never bridged. Each series
//   gaps independently — no channel ever falls back to another column.
// - SOL mode plots the net hero line plus operator-toggled channel lines
//   (fees / tips / BAM; vf-apy-chart.ts owns the toggle state): net keeps
//   the hero treatment (HERO_LINE_WIDTH, ourLine ink, topmost); channel
//   lines are 2px in their theme channel tokens, never the hero width.
//   The tips toggle plots BOTH tips figures as a witness pair — two
//   measurements of ONE flow, same hue: Kobe-settled solid 2px (the
//   figure that actually distributes), block-observed 1px dashed.
// - A legend renders only when 2+ lines are visible (dataviz law: a
//   single series needs no legend); it displays identity only
//   (selectedMode off — the corner toggles are the one visibility
//   control, a second legend-side state would desync them).
// - USD unit (daemon 0.12+ /income rows): the USD series is BLOCK FEES
//   valued at each block's own earn-time SOL/USD price — NOT net income.
//   Expenses and MEV/BAM carry no receipt-time price yet, so no honest
//   per-epoch net-USD figure exists; the view's caption and tooltip state
//   this instead of presenting the line as net. An epoch with zero priced
//   blocks serves null and stays a visible gap.

import type { LineSeriesOption } from "echarts/charts";
import type {
  DataZoomComponentOption,
  GridComponentOption,
  LegendComponentOption,
  MarkLineComponentOption,
  TooltipComponentOption,
} from "echarts/components";
import type { ComposeOption } from "echarts/core";

import {
  alpenglowMarkLine,
  fullChartGrid,
  fullChartSlider,
  fullChartTooltip,
  fullChartXAxis,
  HERO_LINE_WIDTH,
  mergeMarkLines,
  MONO_FONT,
  ourSeriesData,
  pinnedMarkLine,
  SANS_FONT,
  Z_HERO,
} from "./chart-options";
import type { IncomeRow } from "./api";
import { formatCount, formatUsdMicro } from "./format";
import { NOT_SERVED_TEXT, solNumber } from "./income-explorer";
import type { Assembled } from "./series";
import type { ThemeTokens } from "./theme";
import type { EpochTooltipView, TooltipLine } from "./tooltip";
import type { ViewChartInputs } from "./view-options";

export type VfIncomeOption = ComposeOption<
  | LineSeriesOption
  | GridComponentOption
  | LegendComponentOption
  | TooltipComponentOption
  | DataZoomComponentOption
  | MarkLineComponentOption
>;

// Series names double as legend entries, so each states what its line IS.
export const SERIES_INCOME = "net income";
export const SERIES_INCOME_FEES = "block fees";
export const SERIES_INCOME_TIPS_KOBE = "tips · settled (Kobe)";
export const SERIES_INCOME_TIPS_OBSERVED = "tips · block-observed";
export const SERIES_INCOME_BAM = "BAM boost";
export const SERIES_INCOME_USD = "block fees · USD at receipt";

/** Additive channel toggles in the SOL income view; each switches its line(s) on/off. */
export type IncomeChannel = "net" | "fees" | "tips" | "bam";

/** Fixed toggle/series/legend order — identity order never re-derives from toggle state. */
export const INCOME_CHANNEL_ORDER: readonly IncomeChannel[] = ["net", "fees", "tips", "bam"];

/**
 * Footnote while the tips witness pair is visible (operator wording,
 * verbatim): what the two lines are, why a small gap between them is
 * expected, and why neither is validator income.
 */
export const INCOME_TIPS_FOOTNOTE =
  "two witnesses of one flow: block-observed (on-chain, at production) vs Kobe-settled (Jito's post-epoch accounting; what distributes) — a steady small gap is normal (fee + boundary attribution); divergence is signal. Tips flow to stakers at the current 0 bps commission — context, not validator income.";

/** Which unit the income view plots: net SOL (default) or block-fees USD at receipt. */
export type IncomeUnit = "sol" | "usd";

/**
 * USD-mode caption, directly under the plot: the USD line is block fees
 * only — the one figure with a receipt-time valuation — never net.
 */
export const INCOME_USD_FOOTNOTE =
  "USD = block fees, valued at each block's earn-time price — expenses and MEV not yet USD-valued.";

const MICRO_PER_USD = 1_000_000;

/** Micro-USD -> USD as a plain number, display-edge only (plot geometry; every visible figure formats via formatUsdMicro). */
function usdNumber(micro: number): number {
  return micro / MICRO_PER_USD;
}

/** Per-epoch lamports column -> display-edge SOL over the trimmed epoch spine; unserved stays null (gap), never 0. */
function solSeries(a: Assembled, lamportsOf: (epoch: number) => number | null): (number | null)[] {
  return a.epochs.map((epoch) => {
    const lamports = lamportsOf(epoch);
    return lamports === null ? null : solNumber(lamports);
  });
}

/**
 * Per-epoch ledger net_total in SOL over the trimmed epoch spine.
 *
 * An epoch without a served /apy row or with a null net_total_lamports
 * stays null — a visible gap, never 0.
 */
export function incomeNetSolSeries(a: Assembled): (number | null)[] {
  return solSeries(a, (epoch) => a.byEpoch.get(epoch)?.apy?.net_total_lamports ?? null);
}

/** Per-epoch block fees (/income block_fees_lamports) in SOL; unserved stays null. */
export function incomeFeesSolSeries(a: Assembled): (number | null)[] {
  return solSeries(a, (epoch) => a.byEpoch.get(epoch)?.income?.block_fees_lamports ?? null);
}

/**
 * Per-epoch SETTLED tips (/income tips_gross_kobe_lamports) in SOL —
 * Jito's post-epoch accounting, the figure that actually distributes.
 *
 * A null Kobe figure stays a gap; it NEVER falls back to the
 * block-observed column (precision-or-drop: settlement lags production by
 * ~1 epoch, and the two witnesses must stay independent).
 */
export function incomeTipsKobeSolSeries(a: Assembled): (number | null)[] {
  return solSeries(a, (epoch) => a.byEpoch.get(epoch)?.income?.tips_gross_kobe_lamports ?? null);
}

/** Per-epoch block-observed tips (/income tips_gross_lamports) in SOL — the on-chain, at-production witness; unserved stays null. */
export function incomeTipsObservedSolSeries(a: Assembled): (number | null)[] {
  return solSeries(a, (epoch) => a.byEpoch.get(epoch)?.income?.tips_gross_lamports ?? null);
}

/** Per-epoch BAM boost SOL valuation (/apy bam_sol_lamports); unserved stays null. */
export function incomeBamSolSeries(a: Assembled): (number | null)[] {
  return solSeries(a, (epoch) => a.byEpoch.get(epoch)?.apy?.bam_sol_lamports ?? null);
}

/**
 * Per-epoch block fees in USD (receipt-time valued) over the trimmed
 * epoch spine.
 *
 * An epoch without an /income row, on an older daemon (field absent), or
 * with zero priced blocks (served null) stays null — a visible gap,
 * never 0.
 */
export function incomeUsdSeries(a: Assembled): (number | null)[] {
  return a.epochs.map((epoch) => {
    const micro = a.byEpoch.get(epoch)?.income?.block_fees_usd_micro ?? null;
    return micro === null ? null : usdNumber(micro);
  });
}

/**
 * True when any fetched /income row carries the USD valuation field —
 * the gate for rendering the SOL|USD unit switch. Absence (an older
 * daemon) means no switch at all, not a disabled control.
 */
export function usdFieldsPresent(a: Assembled): boolean {
  for (const joined of a.byEpoch.values()) {
    if (joined.income !== undefined && joined.income.block_fees_usd_micro !== undefined) {
      return true;
    }
  }
  return false;
}

/**
 * USD-mode tooltip header: the epoch and its receipt-time block-fee
 * valuation — explicitly "block fees", never "net". Null (zero priced
 * blocks) renders as written absence in title position.
 */
export function incomeUsdTooltipTitle(epoch: number, income: IncomeRow | undefined): string {
  const micro = income?.block_fees_usd_micro ?? null;
  const fees = micro === null ? NOT_SERVED_TEXT : formatUsdMicro(micro);
  return `Epoch ${epoch} · block fees ${fees}`;
}

/**
 * USD-mode tooltip body: the block-fee valuation, its coverage ("priced N
 * of M blocks", from priced_blocks/blocks), and the not-yet-valued
 * disclosure — so the figure can never be read as net-in-USD.
 */
export function incomeUsdTooltipView(income: IncomeRow | undefined, t: ThemeTokens): EpochTooltipView {
  const micro = income?.block_fees_usd_micro ?? null;
  const blocks = income?.blocks ?? null;
  const priced = income?.priced_blocks ?? null;
  const rows: TooltipLine[] = [
    {
      label: "block fees (USD at receipt)",
      value: micro === null ? NOT_SERVED_TEXT : formatUsdMicro(micro),
      color: t.channelFees,
    },
    {
      label: "coverage",
      value:
        blocks !== null && priced !== null
          ? `priced ${formatCount(priced)} of ${formatCount(blocks)} blocks`
          : "block counts not served",
    },
    { label: "expenses and MEV not yet USD-valued", value: "" },
  ];
  return { groups: [{ label: null, marker: null, rows }], trailing: [] };
}

/** Channel-line weight: 2px, deliberately never HERO_LINE_WIDTH (hero identity is ink + z, channels carry more weight but less contrast). */
const CHANNEL_LINE_WIDTH = 2;
/** Channel lines sit below the hero (Z_HERO = 6), matching the main chart's secondary-line altitude. */
const Z_CHANNEL = 4;

interface SolLineSpec {
  channel: IncomeChannel;
  name: string;
  values: (a: Assembled) => (number | null)[];
  color: (t: ThemeTokens) => string;
  width: number;
  dash: "solid" | "dashed";
  z: number;
}

// Fixed series order (net, fees, tips pair, bam). The tips toggle maps to
// TWO series — the witness pair shares the tips hue and differentiates by
// weight + pattern: settled (what distributes) solid 2px, block-observed
// 1px dashed. Nulls gap independently per series.
const SOL_LINES: readonly SolLineSpec[] = [
  {
    channel: "net",
    name: SERIES_INCOME,
    values: incomeNetSolSeries,
    color: (t) => t.ourLine,
    width: HERO_LINE_WIDTH,
    dash: "solid",
    z: Z_HERO,
  },
  {
    channel: "fees",
    name: SERIES_INCOME_FEES,
    values: incomeFeesSolSeries,
    color: (t) => t.channelFees,
    width: CHANNEL_LINE_WIDTH,
    dash: "solid",
    z: Z_CHANNEL,
  },
  {
    channel: "tips",
    name: SERIES_INCOME_TIPS_KOBE,
    values: incomeTipsKobeSolSeries,
    color: (t) => t.channelTips,
    width: CHANNEL_LINE_WIDTH,
    dash: "solid",
    z: Z_CHANNEL,
  },
  {
    channel: "tips",
    name: SERIES_INCOME_TIPS_OBSERVED,
    values: incomeTipsObservedSolSeries,
    // Yellowish sibling of the settled ink (operator): related, not
    // identical — dash + weight + a small warm hue step carry the
    // two-witnesses reading.
    color: (t) => t.channelTipsObserved,
    width: 1,
    dash: "dashed",
    z: Z_CHANNEL,
  },
  {
    channel: "bam",
    name: SERIES_INCOME_BAM,
    values: incomeBamSolSeries,
    color: (t) => t.channelBam,
    width: CHANNEL_LINE_WIDTH,
    dash: "solid",
    z: Z_CHANNEL,
  },
];

/**
 * Index span [lo, hi] of the epochs with a served value in ANY plotted
 * series (SOL: the union over every enabled channel's series, tips
 * counting both witnesses; USD: the one USD series), or null when every
 * epoch is a gap. The income ledger is young relative to the APY spine,
 * so without this the view opens ~99% empty (operator).
 */
export function incomeViewSpan(
  a: Assembled,
  unit: IncomeUnit,
  channels: ReadonlySet<IncomeChannel>,
): { lo: number; hi: number } | null {
  const seriesValues =
    unit === "usd"
      ? [incomeUsdSeries(a)]
      : SOL_LINES.filter((l) => channels.has(l.channel)).map((l) => l.values(a));
  let lo = -1;
  let hi = -1;
  for (const vals of seriesValues) {
    for (let i = 0; i < vals.length; i += 1) {
      if (vals[i] !== null) {
        if (lo < 0 || i < lo) {
          lo = i;
        }
        if (i > hi) {
          hi = i;
        }
      }
    }
  }
  return lo < 0 ? null : { lo, hi };
}

export interface IncomeViewInputs extends ViewChartInputs {
  /** Epoch pinned by a click in this view (drives the amber mark line), or null. */
  pinnedEpoch: number | null;
  /** Plotted unit; callers pass "usd" only when usdFieldsPresent holds. */
  unit: IncomeUnit;
  /** Enabled SOL channels (host-owned toggle state; at least one). Ignored in USD mode. */
  channels: ReadonlySet<IncomeChannel>;
}

/** One income-view line series with the shared null-gap and isolated-point rules. */
function incomeLineSeries(
  name: string,
  values: (number | null)[],
  color: string,
  width: number,
  dash: "solid" | "dashed",
  z: number,
): LineSeriesOption {
  return {
    name,
    type: "line",
    // Isolated-point rule shared with the base hero line (no headline dot).
    data: ourSeriesData(values, null) as LineSeriesOption["data"],
    connectNulls: false,
    showSymbol: true,
    symbol: "circle",
    lineStyle: { color, width, type: dash, opacity: 1 },
    itemStyle: { color },
    emphasis: { itemStyle: { opacity: 1 }, scale: true },
    z,
  };
}

/**
 * Income view over the same epoch domain, slider window, and tooltip
 * chrome as the sibling views. SOL: the enabled channel lines (net hero +
 * toggled channels, tips as the two-witness pair) with a legend at 2+
 * visible lines. USD: the one block-fees-at-receipt line. The clicked
 * epoch renders the shared amber pinned mark line in either unit.
 */
export function buildIncomeOption(i: IncomeViewInputs): VfIncomeOption {
  const { a, theme: t } = i;
  const usd = i.unit === "usd";
  // Default zoom snaps to the served-data span (union over every plotted
  // series): the ledger is young, so a full-spine default window is ~99%
  // gap. A user-adjusted window (anything except the untouched 0-100
  // default) is always respected.
  const span = incomeViewSpan(a, i.unit, i.channels);
  let zoom = i.zoom;
  const untouched = zoom === undefined || (zoom.start === 0 && zoom.end === 100);
  if (span !== null && untouched && a.epochs.length > 1) {
    const denom = a.epochs.length - 1;
    zoom = {
      // Exact fractional percents: floor/ceil padding dragged up to one
      // EMPTY epoch into the window on each side (operator screenshot,
      // epoch-996 hole). ECharts accepts fractions; the window edges land
      // exactly on the served-data span.
      start: Math.max(0, (span.lo / denom) * 100),
      end: Math.min(100, (span.hi / denom) * 100),
    };
  }
  const slider = fullChartSlider(a, t, zoom, i.layout);
  const series: LineSeriesOption[] = usd
    ? [
        incomeLineSeries(
          SERIES_INCOME_USD,
          incomeUsdSeries(a),
          t.ourLine,
          HERO_LINE_WIDTH,
          "solid",
          Z_HERO,
        ),
      ]
    : SOL_LINES.filter((l) => i.channels.has(l.channel)).map((l) =>
        incomeLineSeries(l.name, l.values(a), l.color(t), l.width, l.dash, l.z),
      );
  const markLine = mergeMarkLines(pinnedMarkLine(i.pinnedEpoch, t), alpenglowMarkLine(a.epochs, t));
  if (markLine !== undefined && series[0] !== undefined) {
    series[0].markLine = markLine;
  }
  const option: VfIncomeOption = {
    animation: false,
    textStyle: { fontFamily: SANS_FONT },
    // hasTopLegend false: the legend branch below owns the top override
    // when 2+ lines render; a single-series income plot has no legend
    // and must not inherit the compact legend seat as dead air.
    grid: fullChartGrid(slider !== undefined, i.layout, false),
    xAxis: fullChartXAxis(a, t, i.dateForEpoch),
    yAxis: {
      type: "value",
      name: usd ? "USD" : "SOL",
      nameTextStyle: { fontSize: 11, color: t.axisText, align: "right" },
      axisLabel: { fontSize: 11, fontFamily: MONO_FONT, color: t.axisText },
      axisLine: { show: true, lineStyle: { color: t.axisLine } },
      splitLine: { show: true, lineStyle: { color: t.splitLine } },
      // Auto-scale to the data; peaks are never clipped.
      scale: true,
    },
    tooltip: fullChartTooltip(t, i.tooltipFormatter, i.tooltipAppendTo),
    series,
  };
  // Legend only at 2+ visible lines (dataviz law: a single series needs
  // no legend). It gets its OWN chrome row under the corner controls:
  // top-left collided with the y-axis name/labels, top-right belongs to
  // the HTML corner controls (operator screenshot 2026-07-14) — so row 1
  // is controls, row 2 is the legend (starting past the axis gutter),
  // and the grid drops 24px only while a legend exists.
  // Identity display only — swatches derive from each series' true
  // width/dash, so the tips witness pair reads as one hue, two weights.
  if (series.length >= 2) {
    const grid = option.grid as { top?: number };
    const compact = i.layout?.tier === "compact";
    // Wide: row 1 is the corner-controls overlay, row 2 the legend (the
    // settled 2026-07-14 chrome), grid drops to 64. Compact: the
    // controls leave the canvas for a static row above it (styles.ts
    // vf-chart-compact), so the legend takes the vacated top-left and
    // wraps — same 8/4/4 seat as the base chart's compact legend, same
    // 84 top (three wrapped rows must never overlap the plot). This
    // assignment overwrites the layout's grid top; the compact figure
    // lives here, not in fullChartGrid.
    grid.top = compact ? 84 : 64;
    option.legend = {
      ...(compact ? { top: 8, left: 4, right: 4 } : { top: 34, left: 66 }),
      orient: "horizontal",
      itemWidth: 14,
      itemHeight: 3,
      selectedMode: false,
      data: series.map((s) => ({ name: s.name as string })),
      textStyle: { fontSize: 12, fontWeight: 400, color: t.text, fontFamily: SANS_FONT },
      backgroundColor: "transparent",
      borderWidth: 0,
    };
  }
  if (slider !== undefined) {
    option.dataZoom = slider;
  }
  return option;
}
